import { EmailHistory } from '../email/EmailHistory';
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import TrendingFlatRounded from '@mui/icons-material/TrendingFlatRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import {
  Box,
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { Money } from '../../components/Money';
import { StatusChip } from '../../components/StatusChip';
import { businessDate, dateTime, qty as fmtQty } from '../../lib/format';
import { purchaseReturnDoc, WhatsappA4, WhatsappHistory } from '../whatsapp';
import { AttachmentsPanel } from '../attachments';
import { useShop } from '../../shop/ShopProvider';
import { links } from '../payments/kit/labels';
import {
  BackLink,
  DetailSkeleton,
  ErrorState,
  InfoGrid,
  SectionCard,
  StatCard,
  StatGrid,
} from '../payments/kit/ui';
import { keys } from './api';
import { MoreMenu } from '../../components/MoreMenu';
import { linkedKnown } from '../../components/useLinked';

export function PurchaseReturnDetailPage() {
  const { id = '' } = useParams();
  const { format } = useShop();
  const q = useQuery({
    queryKey: keys.purchaseReturn(id),
    queryFn: () => unwrap(api.GET('/purchase-returns/{id}', { params: { path: { id } } })),
  });

  if (q.isLoading) return <DetailSkeleton />;
  if (q.isError || !q.data)
    return (
      <>
        <BackLink to="/returns?tab=purchase" label="Returns & notes" />
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );

  const r = q.data;
  const variance = Number(r.priceVariance);

  return (
    <>
      <BackLink to="/returns?tab=purchase" label="Returns & notes" />
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h3">{r.number}</Typography>
          <StatusChip status={r.status} />
          <Chip size="small" variant="outlined" label="Purchase return" />
          <Box sx={{ flex: 1 }} />
          <MoreMenu
            linked={[
              linkedKnown('receipts', [r.goodsReceipt], 'Goods receipt'),
              linkedKnown('debitNotes', r.debitNotes),
            ]}
            exportModel="purchase_return"
            exportId={r.id}
          />
        </Stack>
        <Typography color="text.secondary" sx={{ mt: 0.75 }}>
          <Link
            component={RouterLink}
            to={links.contact(r.contact.id, 'supplier')}
            underline="hover"
            sx={{ fontWeight: 600 }}
          >
            {r.contact.name}
          </Link>{' '}
          · from{' '}
          <Link
            component={RouterLink}
            to={links.goodsReceipt(r.goodsReceipt.id)}
            underline="hover"
            sx={{ fontWeight: 600 }}
          >
            {r.goodsReceipt.number}
          </Link>{' '}
          · {businessDate(r.docDate, format)}
        </Typography>
        <Box
          sx={{
            mt: 1.5,
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            '&:empty': { display: 'none' },
          }}
        >
          <WhatsappA4
            type="purchase_return"
            id={r.id}
            number={r.number}
            contact={r.contact}
            vars={{ date: businessDate(r.docDate, format) }}
            doc={() => purchaseReturnDoc(r, format)}
          />
        </Box>
      </Box>

      <StatGrid>
        <StatCard
          label="Stock out"
          value={<Money value={r.totalCost} />}
          hint={`At average cost · ${r.location.name}`}
          icon={<Inventory2Rounded />}
          tone="primary"
        />
        <StatCard
          label="Receipt value"
          value={<Money value={r.grniAmount} />}
          hint="Cleared from goods received"
          icon={<UndoRounded />}
          tone="info"
        />
        <StatCard
          label="Price variance"
          value={<Money value={r.priceVariance} signed />}
          hint={
            variance === 0
              ? 'None'
              : variance > 0
                ? 'Cost above receipt value'
                : 'Cost below receipt value'
          }
          icon={<TrendingFlatRounded />}
          tone={variance === 0 ? 'neutral' : 'warning'}
        />
        <StatCard
          label="Debit notes"
          value={r.debitNotes.length}
          hint={
            r.debitNotes.length ? (
              <Stack direction="row" spacing={1} component="span" sx={{ flexWrap: 'wrap' }}>
                {r.debitNotes.map((n) => (
                  <Link
                    key={n.id}
                    component={RouterLink}
                    to={links.debitNote(n.id)}
                    underline="hover"
                  >
                    {n.number}
                  </Link>
                ))}
              </Stack>
            ) : (
              'Nothing was billed yet'
            )
          }
          icon={<ReceiptLongRounded />}
          tone="success"
        />
      </StatGrid>

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: '1.6fr 1fr' },
          alignItems: 'start',
        }}
      >
        <SectionCard flush title="Returned items" subtitle="Unbilled quantity goes first">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    Unbilled / billed
                  </TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    Cost
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {r.lines.map((l) => (
                  <TableRow key={l.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {l.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {l.sku}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" className="tabular">
                      {fmtQty(l.qty, format)} {l.unitSymbol}
                    </TableCell>
                    <TableCell
                      align="right"
                      className="tabular"
                      sx={{ display: { xs: 'none', md: 'table-cell' }, color: 'text.secondary' }}
                    >
                      {fmtQty(l.qtyUnbilled, format)} / {fmtQty(l.qtyBilled, format)}
                    </TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Money value={l.totalCost} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
        <SectionCard title="Details">
          <InfoGrid
            items={[
              { label: 'Supplier', value: r.contact.name },
              { label: 'Receipt', value: r.goodsReceipt.number },
              { label: 'Date', value: businessDate(r.docDate, format) },
              { label: 'Location', value: r.location.name },
              { label: 'Reason', value: r.reason ?? '—' },
              { label: 'Posted', value: dateTime(r.postedAt, format) },
            ]}
          />
          {r.notes && (
            <Box sx={{ mt: 2.5, p: 1.75, borderRadius: '10px', bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary">
                Notes
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {r.notes}
              </Typography>
            </Box>
          )}
        </SectionCard>
      </Box>
      <AttachmentsPanel recordType="PURCHASE_RETURN" recordId={r.id} sx={{ mt: 3 }} />
      <WhatsappHistory documentType="purchase_return" documentId={r.id} sx={{ mt: 3 }} />
      <EmailHistory documentType="purchase_return" documentId={r.id} sx={{ mt: 3 }} />
    </>
  );
}
